import React from 'react'
import PropTypes from 'prop-types'
import { Row, H2, Button, Subheading } from '@fs/zion-ui'
import { Person } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { REVIEWED_COLOR, IN_PROGRESS_COLOR } from '../../shared/utils/panelConstants'

/**
 * EssentialInformationHeader
 *
 * Header for the Essential Information card. Displays a person icon that changes color based on
 * review mode state, the title with an optional subheading, and an Edit button
 * when the card has been reviewed and is not currently in edit mode.
 *
 * This card is the start of the flow and is never in a disabled state.
 *
 * @param {Object} props
 * @param {Function} props.setIsEditing - Function to enable edit mode.
 * @param {boolean} props.isEditing - Whether the card is currently in edit mode.
 * @param {boolean} [props.reviewed=false] - Whether the card has been reviewed. Changes icon color and button visibility.
 * @returns {JSX.Element} The header element for the essential information card.
 */
function EssentialInformationHeader({ setIsEditing, isEditing, reviewed = false, disableEdit = false }) {
  const { t } = useTranslation()

  const iconColor = reviewed && !isEditing ? REVIEWED_COLOR : IN_PROGRESS_COLOR

  return (
    <Row gutters="xxs" alignItemsY="middle">
      <Person size="sm" background={iconColor} />
      <H2 size="H5" growX>
        {t('essentialInformationCard.title', 'Essential Information')}
        {(!reviewed || isEditing) && (
          <Subheading>{t('essentialInformationForm.subtitle', 'Names and Personal Details')}</Subheading>
        )}
      </H2>
      {reviewed && !isEditing && (
        <Button
          noShrinkX
          onClick={() => setIsEditing(true)}
          linkName="essential-information-edit-btn"
          disabled={disableEdit}
        >
          {t('essentialInformationCard.editButton', 'Edit')}
        </Button>
      )}
    </Row>
  )
}

EssentialInformationHeader.propTypes = {
  setIsEditing: PropTypes.func.isRequired,
  isEditing: PropTypes.bool,
  reviewed: PropTypes.bool,
}

export default EssentialInformationHeader
